import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from './EmptyState';
import { cn } from '@/lib/utils';

export interface DataTableColumn<Row> {
  /** Column identifier — used as the React key + testid suffix. */
  id: string;
  header: ReactNode;
  /** Row-cell renderer. */
  cell: (row: Row) => ReactNode;
  /** Extra classes on the <th> and <td>. */
  className?: string;
}

interface Props<Row> {
  columns: DataTableColumn<Row>[];
  rows: Row[] | undefined;
  getRowKey: (row: Row) => string;
  isLoading?: boolean;
  isError?: boolean;
  /** Rendered inside the table when isError=true. */
  errorState?: ReactNode;
  /** Rendered inside the table when rows.length === 0 and not loading/error. */
  emptyState?: ReactNode;
  /**
   * Optional per-row attributes — data-testid, data-row-id, onClick handlers, etc.
   * Widened to include arbitrary data-* attributes; the runtime spread onto
   * `<tr>` accepts any string-keyed object.
   */
  rowProps?: (row: Row) => Partial<ComponentPropsWithoutRef<'tr'>> & Record<string, unknown>;
  className?: string;
  /** Skeleton row count while loading. */
  skeletonRows?: number;
}

// Consistent table shell: header styling, loading skeleton, empty and error
// states in one place. Callers pass columns + rows + the query state; the
// table handles the rest. Replaces the trio of hand-rolled loading /
// empty / error branches in Issue/Uploads/AuditLog tables (see UI-AUDIT §3).
export function DataTable<Row>({
  columns,
  rows,
  getRowKey,
  isLoading,
  isError,
  errorState,
  emptyState,
  rowProps,
  className,
  skeletonRows = 6,
}: Props<Row>) {
  const colCount = columns.length;

  return (
    <div className={cn('overflow-hidden rounded-md border', className)} data-testid="data-table">
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map((c) => (
              <TableHead
                key={c.id}
                className={cn('text-xs uppercase tracking-wide text-muted-foreground', c.className)}
              >
                {c.header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading && (
            <>
              {Array.from({ length: skeletonRows }).map((_, i) => (
                <TableRow key={`skeleton-${i}`} data-testid="data-table-skeleton-row">
                  {columns.map((c) => (
                    <TableCell key={c.id}>
                      <Skeleton className="h-4 w-full" />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </>
          )}

          {!isLoading && isError && (
            <TableRow>
              <TableCell colSpan={colCount} className="py-8">
                {errorState ?? (
                  <div className="text-center text-sm text-destructive">Couldn&apos;t load.</div>
                )}
              </TableCell>
            </TableRow>
          )}

          {!isLoading && !isError && (rows?.length ?? 0) === 0 && (
            <TableRow>
              <TableCell colSpan={colCount} className="py-8">
                {emptyState ?? <EmptyState title="Nothing here yet" />}
              </TableCell>
            </TableRow>
          )}

          {!isLoading &&
            !isError &&
            (rows ?? []).map((row) => (
              <TableRow key={getRowKey(row)} {...(rowProps ? rowProps(row) : {})}>
                {columns.map((c) => (
                  <TableCell key={c.id} className={c.className}>
                    {c.cell(row)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </div>
  );
}
